import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/services/grc';
import {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Users,
  Bell,
  Shield,
  CloudCog,
  FileSearch,
  Settings,
  LogOut,
  Bug,
  Sun,
  Moon,
  Palette,
  Cloud,
  Activity,
  Crown,
  Building2,
  Lock,
  Layers,
  TrendingUp,
  FileCode2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Target,
  BarChart3,
  Calendar,
  Folder,
  BookOpen,
  GitBranch,
  Github,
  Smartphone,
  Award,
  MessageCircle,
  Fingerprint,
  Database,
  Zap,
  Home,
  Globe,
  Plug,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authAPI } from '@/services/api';
import ThemeSelector from '@/components/ThemeSelector';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import TenantSwitcher from '@/components/TenantSwitcher';

import HelpButton from '@/components/HelpButton';
import SupportButton from '@/components/SupportButton';

const MainLayout = ({ appContext }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { currentTheme, setTheme, theme, isDark } = useTheme();
  const { isSuperAdmin, isModuleEnabled, isTenantAdmin, currentTenant, loading, userRole } = useTenant();
  // Auditor-scope guard: ONLY restrict the sidebar when the caller's role in
  // the *currently selected tenant* is 'auditor'. We deliberately ignore the
  // user's global ``user.role`` because a person can be an owner of tenant A
  // and an auditor on tenant B — the sidebar must reflect the active tenant
  // context, not the historical/global role.
  const isAuditorScoped = !isSuperAdmin && userRole === 'auditor';
  
  // Menu access state - loaded from API for role-based visibility
  const [allowedMenuIds, setAllowedMenuIds] = useState(null); // null = not loaded yet (show all)
  const [userAccessLevel, setUserAccessLevel] = useState('read_write');
  
  // Load user's menu access
  useEffect(() => {
    const loadMenuAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const tenantId = currentTenant?.id;
        const params = tenantId ? `?tenant_id=${tenantId}` : '';
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/menu-access${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.all_access || data.is_super_admin) {
            setAllowedMenuIds(null); // show everything
          } else if (data.menus) {
            // Collect all allowed menu_ids (parent + submenus)
            const ids = new Set();
            data.menus.forEach(m => {
              ids.add(m.menu_id);
              (m.submenus || []).forEach(s => ids.add(s.menu_id));
            });
            setAllowedMenuIds(ids);
          }
          setUserAccessLevel(data.access_level || 'read_write');
        }
      } catch (err) {
        console.error('Failed to load menu access:', err);
      }
    };
    loadMenuAccess();
  }, [currentTenant?.id]);
  
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    compliance: false,
    risk: false,
    audit: false,
    vendors: false,
    security: true,
    cspm: true,
    litesabre: true,
    dpdpa: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentTenantId');
      navigate('/login');
    }
  }, [navigate]);

  // Inactivity timeout: 30 minutes
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
  const inactivityTimer = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  }, [handleLogout]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);

  // Get the base path for current app context
  const getBasePath = () => {
    if (appContext === 'amidala') return '/amidala';
    if (appContext === 'litesabre') return '/litesabre';
    if (appContext === 'litesabre_pro') return '/litesabre-pro';
    return '/grc';
  };

  const basePath = getBasePath();

  // Navigation structure for Amidala CSPM
  const amidalaNavSections = [
    {
      id: 'main',
      items: [
        { path: '/apps', icon: Home, label: 'All Apps', module: null },
        { path: '/amidala/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'amidala' },
      ]
    },
    {
      id: 'cspm',
      label: 'CSPM',
      icon: Cloud,
      items: [
        { path: '/amidala/accounts', icon: Cloud, label: 'Cloud Accounts', module: 'amidala' },
        { path: '/amidala/scan', icon: Activity, label: 'New Scan', module: 'amidala' },
        { path: '/amidala/scans', icon: FileText, label: 'Scan History', module: 'amidala' },
        { path: '/amidala/connectors', icon: Plug, label: 'Connectors', module: 'amidala' },
        { path: '/amidala/catalog', icon: Shield, label: 'Multi-Cloud Catalog', module: 'amidala' },
      ]
    }
  ];

  // Navigation structure for LiteSabre Scanner
  const litesabreNavSections = [
    {
      id: 'main',
      items: [
        { path: '/apps', icon: Home, label: 'All Apps', module: null },
        { path: '/litesabre/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'litesabre' },
      ]
    },
    {
      id: 'scanning',
      label: 'Vulnerability Scanner',
      icon: Bug,
      items: [
        { path: '/litesabre/scan', icon: Bug, label: 'New Scan', module: 'litesabre' },
        { path: '/litesabre/scans', icon: FileText, label: 'Scan History', module: 'litesabre' },
        { path: '/litesabre/vulnerabilities', icon: AlertTriangle, label: 'Vulnerabilities', module: 'litesabre' },
        { path: '/litesabre/sbom', icon: Layers, label: 'SBOM Reports', module: 'litesabre' },
        { path: '/litesabre/scm', icon: GitBranch, label: 'Source Control (SCM)', module: 'litesabre' },
      ]
    }
  ];

  // Navigation structure for LiteSabre Pro
  const litesabreProNavSections = [
    {
      id: 'main',
      items: [
        { path: '/apps', icon: Home, label: 'All Apps', module: null },
        { path: '/litesabre-pro/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'litesabre_pro' },
      ]
    },
    {
      id: 'vapt',
      label: 'VAPT Scanner',
      icon: Target,
      items: [
        { path: '/litesabre-pro/dashboard', icon: Target, label: 'Web DAST', module: 'litesabre_pro' },
        { path: '/litesabre-pro/mobile-analysis', icon: Smartphone, label: 'Mobile Analysis', module: 'litesabre_pro' },
        { path: '/litesabre-pro/ThreatModelling', icon: Shield, label: 'Threat Modeling', module: 'litesabre_pro' },
      ]
    },
    {
      id: 'tools',
      label: 'Tools & Integrations',
      icon: Settings,
      items: [
        { path: '/litesabre-pro/stack-profiles', icon: Layers, label: 'Stack Profiles', module: 'litesabre_pro' },
        { path: '/litesabre-pro/cicd-pipelines', icon: GitBranch, label: 'CI/CD Pipelines', module: 'litesabre_pro' },
        { path: '/litesabre-pro/github-scanning', icon: Github, label: 'GitHub Scanning', module: 'litesabre_pro' },
        { path: '/litesabre-pro/integrations', icon: Settings, label: 'Integrations', module: 'litesabre_pro' },
      ]
    }
  ];

  // GRC Nexus Navigation (original)
  const navSections = [
    {
      id: 'main',
      items: [
        { path: '/apps', icon: Home, label: 'All Apps', module: null },
        { path: '/grc/executive-dashboard', icon: LayoutDashboard, label: 'Executive Dashboard', module: 'grc' },
        { path: '/grc/health', icon: Activity, label: 'System Health', module: null },
        { path: '/admin', icon: Crown, label: 'Admin Portal', module: null, requireAdmin: true },
        { path: '/admin/support-requests', icon: MessageCircle, label: 'Support Requests', module: null, requireAdmin: true },
        { path: '/admin/my-org', icon: Building2, label: 'My Organization', module: null, requireTenantAdmin: true, hideForSuperAdmin: true },
      ]
    },
    {
      id: 'compliance',
      label: 'Compliance',
      icon: Shield,
      items: [
        { path: '/grc/frameworks', icon: Layers, label: 'Frameworks', module: 'grc' },
        { path: '/grc/framework-builder', icon: FileCode2, label: 'Framework Builder', module: 'grc' },
        { path: '/grc/yaml-builder', icon: FileCode2, label: 'YAML Builder', module: 'grc' },
        { path: '/grc/uspcf', icon: Shield, label: 'USPCF Matrix', module: 'grc' },
        { path: '/grc/cross-framework-map', icon: Layers, label: 'Cross-Framework Map', module: 'grc' },
        { path: '/grc/controls', icon: FileText, label: 'Controls', module: 'grc' },
        { path: '/grc/policies', icon: BookOpen, label: 'Policies', module: 'grc' },
        { path: '/grc/policy-templates', icon: FileText, label: 'Policy Templates', module: 'grc' },
        { path: '/grc/documents', icon: Folder, label: 'Documents', module: 'grc' },
        { path: '/grc/evidence', icon: FileSearch, label: 'Evidence', module: 'grc' },
        { path: '/grc/auto-evidence', icon: Zap, label: 'Auto Evidence', module: 'grc' },
      ]
    },
    {
      id: 'risk',
      label: 'Risk Management',
      icon: AlertTriangle,
      items: [
        { path: '/grc/risk', icon: BarChart3, label: 'Risk Dashboard', module: 'grc' },
        { path: '/grc/risk/register', icon: FileText, label: 'Risk Register', module: 'grc' },
        { path: '/grc/risk/assessments', icon: Activity, label: 'Assessments', module: 'grc' },
        { path: '/grc/risk/treatments', icon: Shield, label: 'Treatments', module: 'grc' },
        { path: '/grc/risk/kris', icon: TrendingUp, label: 'KRIs', module: 'grc' },
      ]
    },
    {
      id: 'audit',
      label: 'Audit',
      icon: ClipboardCheck,
      items: [
        { path: '/grc/audit', icon: BarChart3, label: 'Audit Dashboard', module: 'grc' },
        { path: '/grc/audit/schedule', icon: Calendar, label: 'Schedule', module: 'grc' },
        { path: '/grc/audit/engagements', icon: ClipboardCheck, label: 'Engagements', module: 'grc' },
        { path: '/grc/audit/findings', icon: AlertTriangle, label: 'Findings', module: 'grc' },
        { path: '/grc/audit/workpapers', icon: FileText, label: 'Workpapers', module: 'grc' },
        { path: '/grc/continuous-compliance', icon: TrendingUp, label: 'Continuous Compliance', module: 'grc' },
      ]
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: Building2,
      items: [
        { path: '/grc/vendor-assessment', icon: ClipboardCheck, label: 'Vendor Dashboard', module: 'grc' },
        { path: '/grc/vendor-assessment/vendors', icon: Building2, label: 'Vendor Registry', module: 'grc' },
        { path: '/grc/vendor-assessment/assessments', icon: FileText, label: 'Assessments', module: 'grc' },
        { path: '/grc/vendor-assessment/questionnaires', icon: BookOpen, label: 'Questionnaires', module: 'grc' },
      ]
    },
    {
      id: 'training',
      label: 'Security Training',
      icon: Award,
      items: [
        { path: '/grc/my-compliance', icon: CheckCircle, label: 'My Compliance', module: 'grc' },
        { path: '/grc/training', icon: BookOpen, label: 'Training Dashboard', module: 'grc' },
        { path: '/grc/training-admin', icon: Users, label: 'Training Admin', module: 'grc', requireTenantAdmin: true },
      ]
    },
    {
      id: 'dpdpa',
      label: 'Data Privacy (DPDPA)',
      icon: Fingerprint,
      items: [
        { path: '/grc/dpdpa', icon: BarChart3, label: 'DPDPA Dashboard', module: 'dpdpa' },
        { path: '/grc/dpdpa/pre-assessment', icon: ClipboardCheck, label: 'Pre-Assessment', module: 'dpdpa' },
        { path: '/grc/dpdpa/purposes', icon: FileText, label: 'Consent Purposes', module: 'dpdpa' },
        { path: '/grc/dpdpa/consents', icon: Shield, label: 'Consent Records', module: 'dpdpa' },
        { path: '/grc/dpdpa/banners', icon: Layers, label: 'Banner Builder', module: 'dpdpa' },
        { path: '/grc/dpdpa/requests', icon: Users, label: 'Data Subject Requests', module: 'dpdpa' },
        { path: '/grc/dpdpa/breaches', icon: AlertTriangle, label: 'Breach Tracker', module: 'dpdpa' },
        { path: '/grc/dpdpa/breach-detection', icon: Activity, label: 'Breach Detection', module: 'dpdpa' },
        { path: '/grc/dpdpa/pii-scanner', icon: Database, label: 'PII Scanner', module: 'dpdpa' },
        { path: '/grc/dpdpa/dlp-connector', icon: Layers, label: 'DLP Connector', module: 'dpdpa' },
        { path: '/grc/dpdpa/db-connections', icon: Cloud, label: 'DB Connections', module: 'dpdpa' },
        { path: '/grc/dpdpa/data-flow', icon: GitBranch, label: 'Data Flow Mapping', module: 'dpdpa' },
        { path: '/grc/dpdpa/migration', icon: Folder, label: 'Consent Migration', module: 'dpdpa' },
        { path: '/grc/dpdpa/residency-nist', icon: Globe, label: 'Residency & NIST', module: 'dpdpa' },
        { path: '/grc/dpdpa/alert-rules', icon: Zap, label: 'Alert Rules', module: 'dpdpa' },
        { path: '/grc/dpdpa/audit', icon: FileSearch, label: 'DPDPA Audit Trail', module: 'dpdpa' },
      ]
    },
    {
      id: 'other',
      items: [
        { path: '/grc/alerts', icon: Bell, label: 'Alerts', module: null },
        { path: '/grc/audit-trail', icon: FileSearch, label: 'Audit Trail', module: null },
        { path: '/grc/trust-center', icon: Shield, label: 'Trust Center', module: 'grc', requireTenantAdmin: true },
        { path: '/grc/settings', icon: Settings, label: 'Settings', module: null },
      ]
    }
  ];

  // Select appropriate nav sections based on app context
  const getActiveNavSections = () => {
    if (appContext === 'amidala') return amidalaNavSections;
    if (appContext === 'litesabre') return litesabreNavSections;
    if (appContext === 'litesabre_pro') return litesabreProNavSections;
    return navSections;
  };

  const activeNavSections = getActiveNavSections();

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const notifRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => { loadNotifications(); const interval = setInterval(loadNotifications, 60000); return () => clearInterval(interval); }, [loadNotifications]);
  useEffect(() => { const handleClick = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDropdown(false); }; document.addEventListener('mousedown', handleClick); return () => document.removeEventListener('mousedown', handleClick); }, []);

  const handleMarkRead = async (id) => { await markNotificationRead(id); loadNotifications(); };
  const handleMarkAllRead = async () => { await markAllNotificationsRead(); loadNotifications(); };

  // Get app title for header
  const getAppTitle = () => {
    if (appContext === 'amidala') return 'Amidala CSPM';
    if (appContext === 'litesabre') return 'LiteSabre Scanner';
    if (appContext === 'litesabre_pro') return 'LiteSabre Pro';
    return 'GRC Nexus';
  };

  const isItemVisible = (item) => {
    if (isSuperAdmin) {
      return !item.hideForSuperAdmin;
    }
    // External auditors: only the Audit → Schedule item is visible
    if (isAuditorScoped) {
      if (item.path === '/grc/audit/schedule' || item.path === '/apps') return true;
      return false;
    }
    if (item.requireAdmin && !isSuperAdmin) return false;
    if (item.requireTenantAdmin && !isTenantAdmin()) return false;
    
    // Check menu access restriction (if loaded and not all-access)
    if (allowedMenuIds !== null) {
      // Map sidebar path/label to menu_id patterns
      const pathKey = item.path?.replace(/^\/grc\//, '').replace(/-/g, '_').replace(/\//g, '_');
      const labelKey = item.label?.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '');
      
      // Check if this item's path or label matches any allowed menu
      const isAllowed = [...allowedMenuIds].some(id => {
        const idLower = id.toLowerCase();
        return idLower === pathKey || 
               idLower === labelKey ||
               pathKey?.startsWith(idLower) ||
               idLower.includes(pathKey?.split('_')[0] || '');
      });
      
      // Always allow 'All Apps' and sign-out
      if (item.path === '/apps') return true;
      
      if (!isAllowed) return false;
    }
    
    if (!item.module) return true;
    return isModuleEnabled(item.module);
  };

  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderNavItem = (item, isNested = false) => {
    if (!isItemVisible(item)) return null;
    
    const Icon = item.icon;
    const isActive = isPathActive(item.path);
    const isLocked = item.module && !isModuleEnabled(item.module) && !isSuperAdmin;
    
    return (
      <Link
        key={item.path}
        to={isLocked ? '#' : item.path}
        onClick={(e) => isLocked && e.preventDefault()}
        className={`flex items-center gap-3 px-4 py-2 rounded-md transition-all ${
          isActive
            ? 'bg-white/15 text-white'
            : isLocked
            ? 'opacity-50 cursor-not-allowed text-white/60'
            : 'hover:bg-white/10 text-white/80 hover:text-white'
        } ${isNested ? 'text-sm pl-10' : ''}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className={`${isNested ? 'w-4 h-4' : 'w-5 h-5'}`} strokeWidth={1.5} />
        <span className="font-medium flex-1">{item.label}</span>
        {isLocked && <Lock className="w-3 h-3 opacity-50" />}
      </Link>
    );
  };

  const renderSection = (section) => {
    const visibleItems = section.items.filter(isItemVisible);
    if (visibleItems.length === 0) return null;

    // If no label, render items directly (main section)
    if (!section.label) {
      return (
        <div key={section.id} className="space-y-1">
          {visibleItems.map(item => renderNavItem(item))}
        </div>
      );
    }

    // Collapsible section
    const Icon = section.icon;
    const isExpanded = expandedSections[section.id];
    const hasActiveChild = visibleItems.some(item => isPathActive(item.path));

    return (
      <div key={section.id} className="space-y-1">
        <button
          onClick={() => toggleSection(section.id)}
          className={`w-full flex items-center gap-3 px-4 py-2 rounded-md transition-all ${
            hasActiveChild ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5 hover:text-white'
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="font-medium flex-1 text-left">{section.label}</span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 opacity-60" />
          ) : (
            <ChevronRight className="w-4 h-4 opacity-60" />
          )}
        </button>
        {isExpanded && (
          <div className="space-y-1 mt-1">
            {visibleItems.map(item => renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
      {/* Sidebar */}
      <aside className="w-64 text-white flex flex-col" style={{ background: 'var(--sidebar-gradient, linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%))' }}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" style={{ color: 'var(--sidebar-text, white)' }} />
            <div>
              <h1 className="text-xl font-bold font-heading" style={{ color: 'var(--sidebar-text, white)' }}>{getAppTitle()}</h1>
              <p className="text-xs opacity-60">{theme?.name || 'Beskar Platform'}</p>
            </div>
          </div>
        </div>

        {/* Tenant Switcher */}
        <div className="px-4 py-3 border-b border-white/10">
          <TenantSwitcher variant="sidebar" />
        </div>

        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {activeNavSections.map(renderSection)}
        </nav>

        <div className="p-4 border-t border-white/10">
          {/* Theme Toggle */}
          <div className="mb-3 flex items-center justify-between px-2">
            <span className="text-xs opacity-70">Theme</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <ThemeSelector variant="sidebar" />
            </div>
          </div>
          
          <button
            onClick={() => navigate('/apps')}
            className="w-full mb-3 px-4 py-2 text-white/70 hover:text-white hover:bg-white/5 rounded-md text-sm transition-colors flex items-center justify-center gap-2"
            data-testid="back-to-apps-button"
          >
            <Home className="w-4 h-4" />
            All Apps
          </button>
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-sm font-semibold">
                {user.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.full_name}</p>
              <p className="text-xs text-white/60 truncate">
                {isSuperAdmin ? (
                  <span className="flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Super Admin
                  </span>
                ) : currentTenant ? (
                  currentTenant.name
                ) : (
                  user.role
                )}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative" style={{ backgroundColor: 'var(--muted)' }}>
        {/* Top notification bar */}
        <div className="sticky top-0 z-10 flex items-center justify-end px-6 py-2 border-b bg-background/80 backdrop-blur-sm">
          <div className="relative" ref={notifRef}>
            <button onClick={() => { setShowNotifDropdown(!showNotifDropdown); if (!showNotifDropdown) loadNotifications(); }} className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-testid="notification-bell">
              <Bell className="w-5 h-5 opacity-60" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
            {showNotifDropdown && (
              <div className="absolute right-0 top-full mt-1 w-96 bg-white dark:bg-gray-900 border rounded-xl shadow-xl overflow-hidden" data-testid="notif-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 dark:bg-gray-800">
                  <span className="text-sm font-semibold">Notifications</span>
                  {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:underline" data-testid="mark-all-read-btn">Mark all read</button>}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length > 0 ? notifications.slice(0, 8).map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${!n.read ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''}`} onClick={() => { handleMarkRead(n.id); if (n.action_url) navigate(n.action_url); setShowNotifDropdown(false); }} data-testid={`notif-item-${n.id}`}>
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs opacity-60 mt-0.5 line-clamp-1">{n.message}</p>
                      <p className="text-[10px] opacity-40 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  )) : (
                    <div className="px-4 py-8 text-center text-sm opacity-50">No notifications</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <Outlet />
      </main>
      
      {/* Help & Support Buttons */}
      <HelpButton currentModule={
        location.pathname.includes('/grc') ? 'grc' :
        location.pathname.includes('/amidala') ? 'amidala' :
        location.pathname.includes('/litesabre') ? 'litesabre' :
        location.pathname.includes('/osint') ? 'osint' : 'grc'
      } />
      <SupportButton />
    </div>
  );
};

export default MainLayout;
